// Copyright © 2025 The Things Industries B.V.

import React, { useCallback, useMemo, useState } from 'react'
import { defineMessages, FormattedNumber } from 'react-intl'
import { createSelector } from 'reselect'
import { useDispatch, useSelector } from 'react-redux'

import Icon, { IconApplication, IconDevice, IconGateway, IconPlus } from '@ttn-lw/components/icon'
import ModalButton from '@ttn-lw/components/button/modal-button'
import ButtonGroup from '@ttn-lw/components/button/group'
import DeleteModalButton from '@ttn-lw/components/delete-modal-button'
import toast from '@ttn-lw/components/toast'
import Button from '@ttn-lw/components/button'
import Tooltip from '@ttn-lw/components/tooltip'
import Link from '@ttn-lw/components/link'

import FetchTable from '@ttn-lw/containers/fetch-table'

import Message from '@ttn-lw/lib/components/message'
import RequireRequest from '@ttn-lw/lib/components/require-request'

import Pill from '@console/components/pill/index.tti'
import { generateRandomHexColor } from '@console/components/pill/utils.tti'

import sharedMessages from '@ttn-lw/lib/shared-messages'
import attachPromise from '@ttn-lw/lib/store/actions/attach-promise'
import diff from '@ttn-lw/lib/diff'

import {
  checkFromState,
  mayCreateLabels,
  mayDeleteLabel,
  mayUpdateLabel,
} from '@console/lib/feature-checks'

import { deleteLabel, getLabel, getLabelList, updateLabel } from '@console/store/actions/labels.tti'

import {
  selectLabels,
  selectLabelsTotalCount,
  selectSelectedLabel,
} from '@console/store/selectors/labels.tti'

import EditModal from './modal-form.tti'
import CreateLabelPanel from './creating-panel.tti'

import style from './labels-table.tti.styl'

const m = defineMessages({
  deleteWarning:
    'This will permanently delete this label and remove it from all associated entities.',
  applicationsTooltip: '{count, number} {count, plural, one {application} other {applications}}',
  endDevicesTooltip: '{count, number} {count, plural, one {end device} other {end devices}}',
  gatewaysTooltip: '{count, number} {count, plural, one {gateway} other {gateways}}',
  label: 'Label',
  entities: 'Entities',
})

const LabelsTable = () => {
  const dispatch = useDispatch()
  const selectedLabel = useSelector(state => selectSelectedLabel(state))
  const initialEditValues = useMemo(
    () => ({
      ids: {
        label_id: selectedLabel?.ids.label_id,
      },
      name: selectedLabel?.name || '',
      description: selectedLabel?.description || '',
      color: selectedLabel?.color,
    }),
    [selectedLabel],
  )
  const [randomColor, setRandomColor] = useState(initialEditValues?.color)
  const [isCreating, setIsCreating] = useState(false)
  const [editedLabelName, setEditedLabelName] = useState('')
  const [editedLabelDescription, setEditedLabelDescription] = useState('')
  const [editedLabelColor, setEditedLabelColor] = React.useState('')
  const [error, setError] = React.useState(undefined)
  const mayAdd = useSelector(state => checkFromState(mayCreateLabels, state))
  const mayEdit = useSelector(state => checkFromState(mayUpdateLabel, state))
  const mayDelete = useSelector(state => checkFromState(mayDeleteLabel, state))

  const handleInputChange = React.useCallback(
    (name, value) => {
      switch (name) {
        case 'name':
          if (value !== initialEditValues.name) {
            setEditedLabelName(value)
          }
          break
        case 'description':
          if (value !== initialEditValues.description) {
            setEditedLabelDescription(value)
          }
          break
        case 'color':
          if (value !== initialEditValues.color) {
            setEditedLabelColor(value)
            setRandomColor(value || generateRandomHexColor())
          }
          break
        default:
          break
      }
    },
    [setEditedLabelName, setEditedLabelDescription, setEditedLabelColor, initialEditValues],
  )

  const handleEdit = React.useCallback(
    async id => {
      const values = {
        ids: {
          label_id:
            editedLabelName?.toLowerCase().split(' ').join('-') || initialEditValues.ids.label_id,
        },
        name: editedLabelName || initialEditValues.name,
        description: editedLabelDescription || initialEditValues.description,
        color: editedLabelColor || initialEditValues.color,
      }
      const patch = diff(initialEditValues, values)
      try {
        await dispatch(attachPromise(updateLabel(id, patch)))
        toast({
          title: id,
          message: sharedMessages.labelUpdated,
          type: toast.types.SUCCESS,
        })
        setEditedLabelColor('')
        setEditedLabelDescription('')
        setEditedLabelName('')
        setError(undefined)
      } catch (err) {
        setError(err)
        toast({
          title: id,
          message: sharedMessages.labelUpdateError,
          type: toast.types.ERROR,
        })
      }
    },
    [dispatch, initialEditValues, editedLabelName, editedLabelDescription, editedLabelColor],
  )

  const modalData = useCallback(
    id => (
      <RequireRequest requestAction={getLabel(id, ['name', 'description', 'color'])}>
        <EditModal
          initialValues={initialEditValues}
          handleInputChange={handleInputChange}
          randomColor={randomColor}
          setRandomColor={setRandomColor}
          error={error}
          setError={setError}
        />
      </RequireRequest>
    ),
    [initialEditValues, handleInputChange, randomColor, error],
  )

  const handleDelete = React.useCallback(
    async id => {
      try {
        await dispatch(attachPromise(deleteLabel(id)))
        toast({
          title: id,
          message: sharedMessages.labelDeleted,
          type: toast.types.SUCCESS,
        })
      } catch (err) {
        toast({
          title: id,
          message: sharedMessages.labelDeleteError,
          type: toast.types.ERROR,
        })
      }
    },
    [dispatch],
  )

  const headers = React.useMemo(() => {
    const headers = [
      {
        name: 'name',
        displayName: m.label,
        width: '14rem',
        getValue: row => ({
          id: row.ids.label_id,
          name: row.name,
          color: row.color,
        }),
        render: ({ id, name, color }) => (
          <Link to={`/labels/${id}`}>
            <Pill color={color} text={name} />
          </Link>
        ),
        sortable: true,
        sortKey: 'label_id',
      },
      {
        name: 'description',
        width: '20rem',
        displayName: sharedMessages.description,
        render: description => <span className="c-text-neutral-heavy">{description}</span>,
      },
      {
        name: 'entities',
        displayName: m.entities,
        getValue: row => ({
          id: row.ids.label_id,
          entities: row.entities,
        }),
        width: '20rem',
        render: ({ id, entities }) => (
          <div className="d-flex j-start gap-cs-m c-text-neutral-semilight">
            <Tooltip
              delay={0}
              small
              content={
                <Message
                  content={m.applicationsTooltip}
                  values={{ count: entities?.applications?.length || 0 }}
                />
              }
            >
              <Link
                secondary
                to={`${id}?tab=applications`}
                className="d-flex al-center gap-cs-xxs c-text-neutral-heavy td-none"
              >
                <Icon icon={IconApplication} />
                <FormattedNumber value={entities?.applications?.length || 0} />
              </Link>
            </Tooltip>
            <Tooltip
              delay={0}
              small
              content={
                <Message
                  content={m.gatewaysTooltip}
                  values={{ count: entities?.gateways?.length || 0 }}
                />
              }
            >
              <Link
                primary
                to={`${id}?tab=gateways`}
                className="d-flex al-center gap-cs-xxs c-text-neutral-heavy td-none"
              >
                <Icon icon={IconGateway} />
                <FormattedNumber value={entities?.gateways?.length || 0} />
              </Link>
            </Tooltip>
            <Tooltip
              delay={0}
              small
              content={
                <Message
                  content={m.endDevicesTooltip}
                  values={{ count: entities?.end_devices?.length || 0 }}
                />
              }
            >
              <Link
                secondary
                to={`${id}?tab=devices`}
                className="d-flex al-center gap-cs-xxs c-text-neutral-heavy td-none"
              >
                <Icon icon={IconDevice} />
                <FormattedNumber value={entities?.end_devices?.length || 0} />
              </Link>
            </Tooltip>
          </div>
        ),
      },
    ]

    if (mayEdit || mayDelete) {
      headers.push({
        name: 'actions',
        displayName: sharedMessages.actions,
        width: '4rem',
        getValue: row => ({
          id: row.ids.label_id,
          name: row.name,
          edit: handleEdit.bind(null, row.ids.label_id),
          delete: handleDelete.bind(null, row.ids.label_id),
        }),
        render: details =>
          (mayEdit || mayDelete) && (
            <ButtonGroup align="end">
              {mayEdit && (
                <ModalButton
                  message={sharedMessages.edit}
                  modalClassName={style.editModal}
                  onApprove={details.edit}
                  onCancel={() => setError(undefined)}
                  modalData={{
                    title: sharedMessages.editLabel,
                    children: modalData(details.id),
                    buttonMessage: sharedMessages.saveChanges,
                    approveButtonProps: {
                      primary: true,
                    },
                    danger: false,
                  }}
                />
              )}
              {mayDelete && (
                <DeleteModalButton
                  entityId={details.id}
                  entityName={details.name}
                  message={sharedMessages.delete}
                  onApprove={details.delete}
                  defaultMessage={m.deleteWarning}
                />
              )}
            </ButtonGroup>
          ),
      })
    }

    return headers
  }, [handleDelete, modalData, handleEdit, mayDelete, mayEdit])

  const baseDataSelector = createSelector(
    selectLabels,
    selectLabelsTotalCount,
    (labels, totalCount) => ({
      labels,
      totalCount,
      mayAdd: false,
    }),
  )

  const getLabels = React.useCallback(filters => {
    const { query } = filters

    return getLabelList({ ...filters }, ['name', 'description', 'entities', 'color'], {
      isSearch: query.length > 0,
    })
  }, [])

  const handleOpenCreatePanel = React.useCallback(() => {
    setIsCreating(true)
  }, [])

  const createLabelButton = mayAdd ? (
    <Button
      message={sharedMessages.createLabel}
      icon={IconPlus}
      onClick={handleOpenCreatePanel}
      primary
    />
  ) : null

  return (
    <FetchTable
      entity="labels"
      defaultOrder="-created_at"
      headers={headers}
      actionItems={createLabelButton}
      withPanel
      Panel={<CreateLabelPanel isCreating={isCreating} setIsCreating={setIsCreating} />}
      tableTitle={<Message content={sharedMessages.labels} />}
      getItemsAction={getLabels}
      baseDataSelector={baseDataSelector}
      searchable
      clickable={false}
      searchPlaceholderMessage={sharedMessages.searchLabels}
    />
  )
}

export default LabelsTable
